import DataGrid from "../../components/DataGrid";
import { useCallback, useEffect, useMemo } from "react";
import { forwardingOrdersConfig as config } from "./forwardingConfig";
import TableTitleRow from "../../components/TableTitleRow";
import useVisibleColumns from "../../hooks/useVisibleColumns";
import { Forwarding } from "../../types/forwarding";
import { api } from "../../api";
import { deliveryStatusOptions } from "../../helpers/deliveryStatuses";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { forwardingSearchFieldMap } from "../../helpers/searchFieldMap";
import { forwardingOrdersSortFieldMap } from "../../helpers/sortFieldMap";
import { ViewProvider } from "../../contexts/ViewContext";
import { forwardingOrderFromExternalList } from "../../hooks/useSetDataByView/convert";
import { TableDialogs } from "../../components/TableDialogs";
import { getIsNationalFromUrl } from "../../helpers/isNational";
import { extractApiError } from "../../api/errors";
import { useDataGridQuery, useUrlState } from "../../hooks/useUrlState";
import { FORWARDING_ORDERS_DEFAULTS } from "../../helpers/defaultUrl";
import { useSearchParams } from "react-router";
import { ConfirmDialog } from "primereact/confirmdialog";
import { getVisibleSearchFields } from "../../helpers/getVisibleSearchFields";
import { DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";
import { useShowToast } from "../../contexts/ToastContext.tsx";
import { ForwardingOrdersDropdowns } from "../../types";
import {
  cityCountryPairLabel,
  cityCountryPairValue,
  parseCityCountryPairValue,
} from "../../helpers/cityCountryPair.ts";

const columns = config.columns;

export default function ForwardingView() {
  const {
    filters,
    dateFrom,
    dateTo,
    globalFilterValue,
    setGlobalFilterValue,
    showCompletedRows,
  } = useUrlState();

  const { page, limit, sort, order, onPageChange, onSortChange } =
    useDataGridQuery({
      page: FORWARDING_ORDERS_DEFAULTS.page,
      limit: FORWARDING_ORDERS_DEFAULTS.limit,
      sort: FORWARDING_ORDERS_DEFAULTS.sort,
      order: FORWARDING_ORDERS_DEFAULTS.order,
    });

  const [searchParams] = useSearchParams();

  const [visibleColumns, setVisibleColumns] = useVisibleColumns(columns);
  const debouncedSearch = useDebouncedValue(globalFilterValue, 300);
  const showToast = useShowToast();

  // map visible columns to search fields
  const visibleSearchFields = useMemo(
    () => getVisibleSearchFields(visibleColumns, forwardingSearchFieldMap),
    [visibleColumns],
  );

  //sorting
  const orderByField = useMemo(() => {
    const mapped = forwardingOrdersSortFieldMap[sort];
    const base = mapped ?? FORWARDING_ORDERS_DEFAULTS.sort;
    const direction = order === -1 ? "-" : "";
    return `${direction}${base}`;
  }, [sort, order]);

  const filteredLoadingCityAndCountry = useMemo(() => {
    if (!filters.loadingCity) {
      return undefined;
    }
    return parseCityCountryPairValue(filters.loadingCity);
  }, [filters.loadingCity]);

  const filteredUnloadingCityAndCountry = useMemo(() => {
    if (!filters.unloadingCity) {
      return undefined;
    }
    return parseCityCountryPairValue(filters.unloadingCity);
  }, [filters.unloadingCity]);

  const {
    data: rawData,
    isLoading,
    error,
  } = api.endpoints.listForwardingOrdersApiForwardingOrdersGet.useQuery({
    orderBy: `${orderByField},-created_at`,
    limit: limit,
    offset: page * limit,
    search: debouncedSearch || undefined,
    searchFields: visibleSearchFields,
    // filters
    status: filters.deliveryStatus,
    clientId: filters.buyer,
    transporterId: filters.carrier,
    productId: filters.product,
    driverId: filters.driver,
    driverManagerId: filters.driverManager,
    isNational: getIsNationalFromUrl(searchParams),
    entryStatus: !showCompletedRows ? "default,pending" : undefined,
    dateFrom: dateFrom ? dateFrom.toISOString().split("T")[0] : undefined,
    dateTo: dateTo ? dateTo.toISOString().split("T")[0] : undefined,
    loadingCity: filteredLoadingCityAndCountry?.city,
    loadingCountry: filteredLoadingCityAndCountry?.country,
    unloadingCity: filteredUnloadingCityAndCountry?.city,
    unloadingCountry: filteredUnloadingCityAndCountry?.country,
  });

  useEffect(() => {
    if (error) {
      showToast({
        severity: "error",
        summary: "Błąd ładowania danych",
        detail: extractApiError(error),
      });
    }
  }, [error, showToast]);

  const data: Forwarding[] = useMemo(() => {
    if (!rawData) {
      return [];
    }
    return rawData.items.map(forwardingOrderFromExternalList);
  }, [rawData]);

  const { data: buyers } = api.endpoints.listCompaniesApiCompaniesGet.useQuery({
    limit: 10000,
    isBuyer: true,
    orderBy: "name",
  });
  const { data: drivers } = api.endpoints.listDriversApiDriversGet.useQuery({
    limit: 10000,
    orderBy: "last_name",
  });
  const { data: transporters } =
    api.endpoints.listCompaniesApiCompaniesGet.useQuery({
      limit: 10000,
      isTransporter: true,
      orderBy: "name",
    });
  const { data: products } = api.endpoints.listProductsApiProductsGet.useQuery({
    limit: 10000,
    orderBy: "name",
  });
  const { data: locations } =
    api.endpoints.listLocationsApiLocationsGet.useQuery({
      limit: 10000,
      orderBy: "address",
    });
  const { data: cities } =
    api.endpoints.listCitiesApiLocationsCitiesGet.useQuery({
      limit: 10000,
      orderBy: "city",
    });
  const { data: organizationalUsers } =
    api.endpoints.getOrganizationalUsersApiOrganizationalUsersGet.useQuery({
      limit: 10000,
    });

  const dropdownConfigs = useMemo<ForwardingOrdersDropdowns>(() => {
    return {
      buyers:
        buyers?.items?.map((item) => ({
          label: item.name,
          value: item.id,
          data: item,
        })) ?? [],
      transporters:
        transporters?.items?.map((item) => ({
          label: item.name,
          value: item.id,
          data: item,
        })) ?? [],
      products:
        products?.items?.map((item) => ({
          label: item.name,
          value: item.id,
          data: item,
        })) ?? [],
      locations:
        locations?.items.map((location) => ({
          label: `${location.country}, ${location.city}, ${location.address}`,
          value: location.id,
          data: location,
        })) ?? [],
      drivers:
        drivers?.items.map((driver) => ({
          label: `${driver.first_name} ${driver.last_name}`,
          value: driver.id,
          data: driver,
        })) ?? [],
      deliveryStatus: deliveryStatusOptions,
      cities:
        cities?.items.map((city) => ({
          label: cityCountryPairLabel(city),
          value: cityCountryPairValue(city),
          data: city,
        })) ?? [],
      organizationalUsers:
        organizationalUsers?.items?.map((user) => ({
          label: user.email,
          value: user.id,
        })) ?? [],
    };
  }, [
    buyers,
    drivers,
    transporters,
    products,
    locations,
    cities,
    organizationalUsers,
  ]);

  const handleSortChange = useCallback(
    (e: DataTableSortEvent) => onSortChange(e.sortField, e.sortOrder ?? 1),
    [onSortChange],
  );
  const handlePageChange = useCallback(
    (e: DataTablePageEvent) => onPageChange(e.page ?? 0, e.rows ?? limit),
    [onPageChange, limit],
  );

  return (
    <ViewProvider view={config.view}>
      <TableDialogs />
      <ConfirmDialog />
      <TableTitleRow<ForwardingOrdersDropdowns, "forwardingOrders">
        visibleColumns={visibleColumns}
        columns={columns}
        setVisibleColumns={setVisibleColumns}
        globalFilterValue={globalFilterValue}
        setGlobalFilterValue={setGlobalFilterValue}
        config={config}
        dropdownConfigs={dropdownConfigs}
      />
      <DataGrid<ForwardingOrdersDropdowns, "forwardingOrders">
        data={data}
        page={page}
        onPage={handlePageChange}
        rowsPerPage={limit}
        totalRecords={rawData?.total || 0}
        sortField={sort}
        sortOrder={order}
        onSort={handleSortChange}
        visibleColumns={visibleColumns}
        config={config}
        dropdownConfigs={dropdownConfigs}
        globalFilterValue={globalFilterValue}
        isLoading={isLoading}
      ></DataGrid>
    </ViewProvider>
  );
}
